import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { foyerApi, type UpsalesEvent } from "../http/foyer";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";

/**
 * What Foyer sends - or, on dev, WOULD send - Nenda's Upsales CRM (ORG-4,
 * `docs/design/organisation-plans.md`). One card per sale event (an approved
 * plan, an upgrade) with its planned steps in words and the exact request
 * bodies on demand, so a dry run can be checked line by line before the one
 * live run. There is no send button here by design: a live write is an
 * explicit `scripts/upsales-sync.ts --live` (Upsales has no sandbox).
 */

const STATUS_COLOR: Record<
  UpsalesEvent["status"],
  "default" | "info" | "success" | "error"
> = {
  pending: "default",
  planned: "info",
  sent: "success",
  failed: "error",
};

const MODES = ["off", "dry_run", "live"] as const;
type Mode = (typeof MODES)[number];
const isMode = (m: string): m is Mode =>
  (MODES as readonly string[]).includes(m);

export default function UpsalesSyncPage() {
  const [data, setData] = useState<{
    mode: string;
    events: UpsalesEvent[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { t } = useTranslation("analytics");
  const fmt = useFormat();

  const load = useCallback(async () => {
    try {
      setData(await foyerApi.upsalesEvents());
    } catch (e) {
      setError(errorText(e, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function replan(id: string) {
    setBusy(id);
    try {
      await foyerApi.replanUpsalesEvent(id);
      await load();
    } catch (e) {
      setError(errorText(e, t));
    } finally {
      setBusy(null);
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress size={24} />;

  return (
    <Stack spacing={2} sx={{ maxWidth: 980 }}>
      <Alert severity={data.mode === "live" ? "warning" : "info"}>
        {isMode(data.mode) ? t(`upsalesSyncPage.mode.${data.mode}`) : data.mode}
      </Alert>
      {data.events.length === 0 && (
        <Typography sx={{ color: tokens.inkMuted }}>
          {t("upsalesSyncPage.empty")}
        </Typography>
      )}
      {data.events.map((ev) => (
        <Paper
          key={ev.id}
          variant="outlined"
          sx={{ p: 2 }}
          data-testid={`upsales-event-${ev.id}`}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                {t("upsalesSyncPage.heading", {
                  organisation:
                    ev.organisation ?? t("upsalesSyncPage.unknownOrganisation"),
                  provider: ev.provider ?? ev.contentOwnerId,
                })}
              </Typography>
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("upsalesSyncPage.details", {
                  kind:
                    ev.kind === "subscription"
                      ? t("upsalesSyncPage.kind.subscription")
                      : t("upsalesSyncPage.kind.upgrade"),
                  plan: ev.planLabel,
                  orgNumber: ev.orgNumber,
                  createdAt: fmt.dateTime(ev.createdAt),
                  id: ev.id,
                })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={t(`upsalesSyncPage.status.${ev.status}`)}
                color={STATUS_COLOR[ev.status]}
              />
              {ev.status !== "sent" && (
                <Button
                  size="small"
                  disabled={busy !== null}
                  onClick={() => void replan(ev.id)}
                >
                  {t("upsalesSyncPage.rebuildDryRun")}
                </Button>
              )}
            </Stack>
          </Stack>
          {ev.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {ev.error}
            </Alert>
          )}
          {ev.plan.length > 0 && (
            <Box
              component="ol"
              sx={{ mt: 1.5, mb: 0, pl: 3, typography: "body2" }}
            >
              {ev.plan.map((step) => (
                <li key={step.key}>
                  <strong>
                    {step.op === "reuse"
                      ? t("upsalesSyncPage.step.reuse")
                      : step.op === "create"
                        ? t("upsalesSyncPage.step.create")
                        : t("upsalesSyncPage.step.update")}
                  </strong>{" "}
                  {step.note}
                  {typeof ev.result[step.key] === "number" && (
                    <Typography
                      variant="meta"
                      component="span"
                      sx={{ ml: 1, color: tokens.inkMuted }}
                    >
                      {t("upsalesSyncPage.upsalesId", {
                        id: String(ev.result[step.key]),
                      })}
                    </Typography>
                  )}
                </li>
              ))}
            </Box>
          )}
          {ev.plan.length > 0 && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setOpen(open === ev.id ? null : ev.id)}
            >
              {open === ev.id
                ? t("upsalesSyncPage.hideRequests")
                : t("upsalesSyncPage.showRequests")}
            </Button>
          )}
          {open === ev.id && (
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: tokens.rowAlt,
                typography: "meta",
                overflowX: "auto",
                borderRadius: 1,
              }}
            >
              {JSON.stringify(ev.plan, null, 2)}
            </Box>
          )}
        </Paper>
      ))}
    </Stack>
  );
}
